/** Platform default when tenant has no custom logo. */
export const PLATFORM_BRAND = {
  businessName: 'cbonline app',
  platformName: 'cbonline app',
  defaultLogoPath: '/branding/cb-app-logo-circle.svg',
  subtitle: 'Professional Business Report',
};

let sessionBranding = { ...PLATFORM_BRAND, logoUrl: null, phone: '', address: '', hasCustomLogo: false };

export function brandingFromUser(user) {
  const t = user?.tenant;
  const name = (t?.name && String(t.name).trim()) || PLATFORM_BRAND.businessName;
  const logoUrl = t?.logoUrl && String(t.logoUrl).trim() ? t.logoUrl : null;

  return {
    businessName: name,
    displayName: name,
    platformName: PLATFORM_BRAND.platformName,
    logoUrl,
    defaultLogoPath: PLATFORM_BRAND.defaultLogoPath,
    subtitle: PLATFORM_BRAND.subtitle,
    phone: t?.phone || '',
    address: t?.address || '',
    hasCustomLogo: !!logoUrl,
  };
}

export function setSessionBranding(user) {
  sessionBranding = user ? brandingFromUser(user) : { ...PLATFORM_BRAND, logoUrl: null, phone: '', address: '', hasCustomLogo: false };
}

export function getSessionBranding() {
  return sessionBranding;
}

export function resolveExportBranding(options) {
  return options?.branding || sessionBranding;
}

async function fetchAsDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) return null;
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

function svgToPngDataUrl(svgDataUrl, size = 220) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.clearRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = svgDataUrl;
    } catch {
      resolve(null);
    }
  });
}

function imageFormatFromDataUrl(dataUrl) {
  const m = String(dataUrl).match(/^data:image\/(\w+);/i);
  if (!m) return 'PNG';
  const f = m[1].toLowerCase();
  if (f === 'jpg' || f === 'jpeg') return 'JPEG';
  if (f === 'png') return 'PNG';
  if (f === 'webp') return 'WEBP';
  if (f === 'gif') return 'GIF';
  return 'PNG';
}

/** Logo for jsPDF — tenant upload or platform default. */
export async function loadBrandingLogoDataUrl(branding) {
  const b = branding || sessionBranding;
  if (b.logoUrl) {
    if (String(b.logoUrl).startsWith('data:image/')) {
      if (String(b.logoUrl).startsWith('data:image/svg+xml')) {
        return svgToPngDataUrl(b.logoUrl, 240);
      }
      return b.logoUrl;
    }
    return fetchAsDataUrl(b.logoUrl);
  }

  const base = window.location.origin;
  const preferred = [
    `${base}${b.defaultLogoPath || PLATFORM_BRAND.defaultLogoPath}`,
    `${base}/branding/cb-app-mark.svg`,
    `${base}/favicon.svg`,
  ];
  for (const url of preferred) {
    const raw = await fetchAsDataUrl(url);
    if (!raw) continue;
    if (String(raw).startsWith('data:image/svg+xml')) {
      const png = await svgToPngDataUrl(raw, 240);
      if (png) return png;
    } else {
      return raw;
    }
  }
  return null;
}

export function pdfImageFormat(logoDataUrl) {
  return imageFormatFromDataUrl(logoDataUrl);
}

export function brandingFooterLine(branding) {
  const b = branding || sessionBranding;
  if (b.hasCustomLogo) {
    return `Generated by ${b.businessName}`;
  }
  return `Generated by ${b.platformName}`;
}

export function brandingPoweredByLine(branding) {
  const b = branding || sessionBranding;
  if (b.hasCustomLogo && b.businessName !== PLATFORM_BRAND.businessName) {
    return `Powered by ${PLATFORM_BRAND.platformName}`;
  }
  return null;
}
